// Bus-number helpers — ported from `lu_reconciliation_requirements_v3.md` §7 (outlier detection), §8
// (sequence suggestion) and §9 (logical sort). ONLY the "add a box within an address" subset is used by
// the Address-Validation app: given the bus_numbers already present in a building, suggest the next ones
// following the existing pattern, and compare/normalise for duplicate detection. Pure + dependency-free.
//
// NB: the §8 floor/wing dimension is modelled as the LEFTMOST varying single digit position; cases that
// would overflow a digit position (floor > 9) fall back to sequential increment (the doc's safety path,
// it caps at 99). Exotic multi-prefix data degrades gracefully to sequential — never throws.
/** §7.1 — parse a bus_number as (letter prefix, digit string, int). null when not "letters?+digits". */
export function parseBusNumber(raw) {
    const s = (raw ?? '').trim();
    const m = /^([A-Za-z]+)?[\s.\-_]*(\d+)$/.exec(s);
    if (!m)
        return null;
    return { prefix: m[1] ?? '', digits: m[2], value: parseInt(m[2], 10) };
}
const NORMALISE_PREFIXES = ['bte', 'box', 'apt', 'app', 'flat', 'unit'];
/**
 * §7.3 — normalise for duplicate comparison: lowercase, strip a leading bte/box/apt/app/flat/unit token
 * (+ following separators), strip leading zeros; empty result → "0". So "Bte 01" == "bte1" == "1".
 */
export function normalizeBusNumber(raw) {
    let v = (raw ?? '').trim().toLowerCase();
    for (const p of NORMALISE_PREFIXES) {
        if (v.startsWith(p)) {
            v = v.slice(p.length).replace(/^[\s.\-_]+/, '');
            break;
        }
    }
    v = v.replace(/^0+/, '');
    return v === '' ? '0' : v;
}
/** §9 — logical sort key: parseable < non-standard < empty; parseable by (prefix, numeric value). */
export function busNumberSortKey(raw) {
    const s = (raw ?? '').trim();
    if (s === '')
        return [2, '', 0, ''];
    const p = parseBusNumber(s);
    if (p)
        return [0, p.prefix.toLowerCase(), p.value, ''];
    return [1, '', 0, s.toLowerCase()];
}
export function compareBusNumbers(a, b) {
    const ka = busNumberSortKey(a);
    const kb = busNumberSortKey(b);
    for (let i = 0; i < ka.length; i++) {
        if (ka[i] < kb[i])
            return -1;
        if (ka[i] > kb[i])
            return 1;
    }
    return 0;
}
function mostFrequent(items) {
    const counts = new Map();
    for (const it of items)
        counts.set(it, (counts.get(it) ?? 0) + 1);
    let best;
    let bestN = -1;
    for (const [k, n] of counts) {
        if (n > bestN) {
            best = k;
            bestN = n;
        }
    }
    return best;
}
function countOf(items, value) {
    return items.reduce((acc, it) => acc + (it === value ? 1 : 0), 0);
}
/**
 * §7 — detect outlier bus_numbers (indices into `values`). Combines §7.1 non-dominant prefix, §7.2 wrong
 * digit length (skipped when the numeric range spans a power-of-10 boundary), and §7.3 normalised duplicates.
 */
export function detectBusNumberOutliers(values) {
    const outliers = new Set();
    const parsed = values.map((v) => parseBusNumber(v));
    // §7.1 — dominant prefix (only when it occurs more than once).
    const prefixes = parsed.filter((p) => p != null).map((p) => p.prefix);
    const domPrefix = mostFrequent(prefixes);
    if (domPrefix !== undefined && countOf(prefixes, domPrefix) > 1) {
        parsed.forEach((p, i) => {
            if (p && p.prefix !== domPrefix)
                outliers.add(i);
        });
    }
    // §7.2 — wrong digit length within the dominant-prefix group (excluding already-flagged).
    const domIdx = parsed
        .map((p, i) => ({ p, i }))
        .filter(({ p, i }) => p != null && !outliers.has(i) && (domPrefix === undefined || p.prefix === domPrefix));
    if (domIdx.length > 1) {
        const values_ = domIdx.map(({ p }) => p.value);
        const minV = Math.min(...values_);
        const maxV = Math.max(...values_);
        const spansPowerOfTen = String(minV).length !== String(maxV).length; // §7.2 exception
        if (!spansPowerOfTen) {
            const lengths = domIdx.map(({ p }) => p.digits.length);
            const domLen = mostFrequent(lengths);
            if (domLen !== undefined && countOf(lengths, domLen) > 1) {
                domIdx.forEach(({ p, i }) => {
                    if (p.digits.length !== domLen)
                        outliers.add(i);
                });
            }
        }
    }
    // §7.3 — normalised duplicates: keep the dominant prefix+length, flag the other(s).
    const domLenAll = mostFrequent(parsed.filter((p) => p != null).map((p) => p.digits.length));
    const seen = new Map(); // normalised → kept index
    values.forEach((v, i) => {
        if (outliers.has(i))
            return;
        const norm = normalizeBusNumber(v);
        const prev = seen.get(norm);
        if (prev === undefined) {
            seen.set(norm, i);
            return;
        }
        // Decide which to keep: the one matching dominant prefix AND dominant digit length.
        const fits = (idx) => {
            const p = parsed[idx];
            return !!p && (domPrefix === undefined || p.prefix === domPrefix) && p.digits.length === domLenAll;
        };
        if (fits(i) && !fits(prev)) {
            outliers.add(prev);
            seen.set(norm, i);
        }
        else {
            outliers.add(i);
        }
    });
    return outliers;
}
/**
 * §8 — suggest `n` new bus_numbers following the building's existing pattern. Outliers (§7) are excluded
 * from the pattern analysis; the leftmost varying digit position is the floor/wing dimension, inner varying
 * positions are the unit dimension (existing unit combinations are repeated for each new floor). Never
 * returns a value whose normalised form already exists (§8.12). Falls back to sequential increment for
 * single-value / no-varying-position / unparseable / floor-overflow cases.
 */
export function suggestBusNumbers(existing, n, 
/** Extra numbers that are TAKEN but must not shape the pattern — the rest of the building's postal
 *  addresses, and the operator renames held in address_mapping_table (user rule 2026-08-04). They
 *  only ever block a suggestion; feeding them into the analysis pool would double-count the same
 *  physical box (a DMP "1" that Proximus reads as "03") and blur the floor/wing detection. */
reserved = []) {
    if (n <= 0)
        return [];
    const result = [];
    // §8.12 — never duplicate (normalised), against the analysed set AND everything reserved.
    const taken = new Set([...existing, ...reserved].map(normalizeBusNumber));
    const push = (s) => {
        const norm = normalizeBusNumber(s);
        if (!taken.has(norm)) {
            result.push(s);
            taken.add(norm);
        }
    };
    // Analysis pool = existing minus outliers (§8.1).
    const outliers = detectBusNumberOutliers(existing);
    const pool = existing.filter((_, i) => !outliers.has(i));
    const parsed = pool.map(parseBusNumber).filter((p) => p != null);
    // §8.2 — nothing parses → sequential ints after the highest existing int.
    const maxInt = parsed.length
        ? Math.max(...parsed.map((p) => p.value))
        : Math.max(0, ...existing.map((e) => parseBusNumber(e)?.value ?? 0));
    const sequentialFallback = (prefix, padLen, from) => {
        let v = from;
        let guard = 0;
        while (result.length < n && guard++ < 100000) {
            push(prefix + String(++v).padStart(padLen, '0'));
        }
    };
    if (parsed.length === 0) {
        sequentialFallback('', 1, maxInt);
        return result.slice(0, n);
    }
    // §8.3/§8.4 — dominant prefix + pad length.
    const domPrefix = mostFrequent(parsed.map((p) => p.prefix)) ?? '';
    const domGroup = parsed.filter((p) => p.prefix === domPrefix);
    const padLen = mostFrequent(domGroup.map((p) => p.digits.length)) ?? 1;
    const domMax = Math.max(...domGroup.map((p) => p.value));
    // §8.5 — single value → increment by 1.
    if (domGroup.length === 1) {
        sequentialFallback(domPrefix, padLen, domMax);
        return result.slice(0, n);
    }
    // §8.6/§8.7 — pad to pad length, find varying vs fixed digit positions.
    const padded = domGroup.map((p) => String(p.value).padStart(padLen, '0'));
    const posSets = [];
    for (let i = 0; i < padLen; i++)
        posSets.push(new Set(padded.map((s) => s[i])));
    const varying = posSets.map((set, i) => (set.size > 1 ? i : -1)).filter((i) => i >= 0);
    // §8.8 — no varying positions → increment max.
    if (varying.length === 0) {
        sequentialFallback(domPrefix, padLen, domMax);
        return result.slice(0, n);
    }
    // §8.9/§8.10 — leftmost varying = floor/wing; inner varying = unit; repeat existing unit combos per floor.
    const floorPos = varying[0];
    const innerPos = varying.slice(1);
    const template = padded[0].split('');
    const unitCombos = Array.from(new Set(padded.map((s) => innerPos.map((i) => s[i]).join(''))));
    const floorMax = Math.max(...padded.map((s) => parseInt(s[floorPos], 10)));
    let floor = floorMax + 1;
    // §8.11 — single-digit floor position caps at 9 here (the doc caps the wing at 99); overflow → fallback.
    while (result.length < n && floor <= 9) {
        for (const combo of unitCombos) {
            if (result.length >= n)
                break;
            const chars = template.slice();
            chars[floorPos] = String(floor);
            innerPos.forEach((pos, k) => {
                chars[pos] = combo[k];
            });
            push(domPrefix + chars.join(''));
        }
        floor++;
    }
    // Still short (floor cap hit) → sequential after the dominant max.
    if (result.length < n)
        sequentialFallback(domPrefix, padLen, domMax);
    return result.slice(0, n);
}
//# sourceMappingURL=bus-number.js.map